/**
 * Notification service for handling notification-related API calls
 */
import apiService from './apiService';

const BASE_ENDPOINT = '/notifications';

/**
 * Service for handling notification-related operations
 */
const notificationService = {
  /**
   * Get all notifications with pagination and filtering
   * @param {Object} options - Pagination and filter options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Number of items per page
   * @param {string} options.status - Filter by status (new, viewed, resolved)
   * @param {string} options.type - Filter by type (leak_detected, zero_reading, etc.)
   * @returns {Promise<Object>} - Paginated notifications
   */
  getNotifications(options = {}) {
    const { page = 1, limit = 10, status, type, deviceId } = options;
    let endpoint = `${BASE_ENDPOINT}?page=${page}&limit=${limit}`;
    
    if (status) {
      endpoint += `&status=${status}`;
    }
    
    if (type) {
      endpoint += `&type=${type}`;
    }

    if (deviceId) {
      endpoint += `&deviceId=${encodeURIComponent(deviceId)}`;
    }
    
    return apiService.get(endpoint);
  },
  
  /**
   * Get cross-site monitor notifications (leaks, thresholds, signal strength)
   * @param {Object} options
   * @param {string} [options.startDate] - YYYY-MM-DD reporting date (defaults to yesterday on server)
   * @param {string} [options.endDate] - YYYY-MM-DD reporting date (defaults to startDate)
   * @param {string[]} [options.siteIds] - Restrict to devices in these sites
   * @param {string} [options.status] - open (default), new, viewed, resolved or all
   * @returns {Promise<Object>} - { startDate, endDate, total, truncated, notifications }
   */
  getMonitorNotifications(options = {}) {
    const { startDate, endDate, siteIds, status } = options;
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (Array.isArray(siteIds) && siteIds.length > 0) params.set('siteIds', siteIds.join(','));
    if (status) params.set('status', status);
    const qs = params.toString();
    return apiService.get(`${BASE_ENDPOINT}/monitor${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get a specific notification by ID
   * @param {string} id - Notification ID
   * @returns {Promise<Object>} - Notification details
   */
  getNotificationById(id) {
    return apiService.get(`${BASE_ENDPOINT}/${id}`);
  },
  
  /**
   * Update notification status
   * @param {string} id - Notification ID
   * @param {string} status - New status (viewed, resolved)
   * @returns {Promise<Object>} - Updated notification
   */
  updateNotificationStatus(id, status) {
    return apiService.put(`${BASE_ENDPOINT}/${id}/status`, { status });
  },
  
  /**
   * Delete a notification
   * @param {string} id - Notification ID
   * @returns {Promise<Object>} - Success response
   */
  deleteNotification(id) {
    return apiService.delete(`${BASE_ENDPOINT}/${id}`);
  },
  
  /**
   * Get notification counts by status
   * @returns {Promise<Object>} - Counts of notifications by status
   */
  getNotificationCounts() {
    return apiService.get(`${BASE_ENDPOINT}/counts`);
  },
  
  /**
   * Run leak detection manually
   * @returns {Promise<Object>} - Success response
   */
  runLeakDetection() {
    return apiService.post(`${BASE_ENDPOINT}/detect-leaks`);
  },
  
  /**
   * Run daily thresholds generation (admin only)
   * @param {string} [date] - Optional YYYY-MM-DD date to run for; defaults to yesterday on server
   * @returns {Promise<Object>} - Success response
   */
  runDailyThresholds(date) {
    const payload = date ? { date } : {};
    return apiService.post(`/daily-jobs/thresholds/run`, payload);
  },
  
  /**
   * Run previous-day leak analysis (admin only)
   * @param {string} [date] - Optional YYYY-MM-DD date to run for; defaults to yesterday on server
   * @returns {Promise<Object>} - Success response
   */
  runLeakDaily(date) {
    const payload = date ? { date } : {};
    return apiService.post(`/daily-jobs/leaks/run`, payload);
  },

  /**
   * Run notification digest email (admin only)
   * @param {number} [hours] - Optional lookback hours for digest window (first run only if job state missing)
   * @returns {Promise<Object>} - Success response
   */
  runNotificationDigest(hours) {
    const payload = hours !== undefined && hours !== null ? { hours } : {};
    return apiService.post(`/daily-jobs/digest/run`, payload);
  },
  
  /**
   * Update leak detection schedule
   * @param {string} schedule - Cron schedule expression
   * @returns {Promise<Object>} - Success response
   */
  updateLeakDetectionSchedule(schedule) {
    return apiService.put(`${BASE_ENDPOINT}/schedule`, { schedule });
  },

  /**
   * Update daily thresholds schedule
   * @param {string} schedule - Cron schedule expression
   * @returns {Promise<Object>} - Success response
   */
  updateDailyThresholdsSchedule(schedule) {
    return apiService.put(`${BASE_ENDPOINT}/schedule/thresholds`, { schedule });
  },

  /**
   * Update previous-day leak analysis schedule
   * @param {string} schedule - Cron schedule expression
   * @returns {Promise<Object>} - Success response
   */
  updateLeakDailySchedule(schedule) {
    return apiService.put(`${BASE_ENDPOINT}/schedule/leaks`, { schedule });
  },

  /**
   * Run signal strength check (admin only)
   * @param {string} [date] - Optional YYYY-MM-DD date to run for; defaults to yesterday on server
   * @returns {Promise<Object>} - Success response
   */
  runSignalStrengthCheck(date) {
    const payload = date ? { date } : {};
    return apiService.post(`${BASE_ENDPOINT}/check-signal-strength`, payload);
  },

  /**
   * Update signal strength check schedule
   * @param {string} schedule - Cron schedule expression
   * @returns {Promise<Object>} - Success response
   */
  updateSignalStrengthSchedule(schedule) {
    return apiService.put(`${BASE_ENDPOINT}/schedule/signal-strength`, { schedule });
  },

  /**
   * Update notification digest schedule
   * @param {string} schedule - Cron schedule expression
   * @returns {Promise<Object>} - Success response
   */
  updateNotificationDigestSchedule(schedule) {
    return apiService.put(`${BASE_ENDPOINT}/schedule/digest`, { schedule });
  },
  
  /**
   * Get leak detection settings
   * @returns {Promise<Object>} - Settings
   */
  getLeakDetectionSettings() {
    return apiService.get(`${BASE_ENDPOINT}/config/settings`);
  }
};

export default notificationService;
